import React, { FC, useState } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { DEMO_STAY_LISTINGS } from "data/listings";
import { StayDataType } from "data/types";
import StartRating from "components/StartRating/StartRating";
import Badge from "shared/Badge/Badge";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { updatePropertiesListingStatus } from "../../redux/reducers/Properties/PropertiesSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import successHandler from "utils/helpers/SuccessHandler";
import { currencySymbol } from "shared/CurrencySymbol";
import { MdLocationPin } from "react-icons/md";

export interface StayCardV5Props {
  className?: string;
  data?: StayDataType;
  size?: "default" | "small";
  setAllData: React.Dispatch<React.SetStateAction<StayDataType[]>>;
  stayListings: StayDataType[];
}

const DATA = DEMO_STAY_LISTINGS[0];

const StayCardV5: FC<StayCardV5Props> = ({
  size = "default",
  className = "",
  data = DATA,
  setAllData,
  stayListings,
}) => {
  const {
    place_images,
    address,
    title,
    href,
    price,
    reviewStart,
    reviewCount,
    id,
    property_state,
    active,
    property_category,
    property_type,
    slug,
    reviewTotal,
    totalBedrooms,
  } = data;

  const dispatch = useDispatch<AppDispatch>();
  const [activeLoading, setActiveLoading] = useState(false);

  const handleStatusUpdate = async (status: boolean, id: any) => {
    setActiveLoading(true);
    const payload = {
      property: {
        active: !status,
        id: id,
      },
    };
    const action = await dispatch(updatePropertiesListingStatus(payload));
    if (action.payload.success) {
      const modifiedArr = stayListings?.map((item: any) =>
        item.id === id ? { ...item, active: !status } : item
      );
      setAllData(modifiedArr);
    }
    successHandler(
      active
        ? "The property has been paused!"
        : "The property has been activated!"
    );
    setActiveLoading(false);
  };

  const handleToggleListingStatus = () => {
    if (active !== undefined) {
      handleStatusUpdate(active, id);
    }
  };


  const getStatusBadge = (status: string | undefined) => {
    switch (status) {
      case "waiting_for_approval":
        return {
          label: "Waiting for approval",
          className: "bg-yellow-100 text-yellow-800",
        };
      case "approved":
        return {
          label: "Approved",
          className: "bg-green-700 text-white",
        };
      default:
        return {
          label: "",
          className: "",
        };
    }
  };

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full">
        <GallerySlider
          uniqueID={`StayCard_${id}`}
          ratioClass="aspect-w-4 aspect-h-3 "
          galleryImgs={place_images}
          href={href}
        />
        <div
          className={`absolute left-3 top-3 z-[1] px-5 rounded-md ${getStatusBadge(property_state).className
            }`}
        >
          {getStatusBadge(property_state).label}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="p-4">
        <div className="flex items-center text-sm font-semibold">
          <span className="line-clamp-2">{title}</span>
        </div>
        <div className="flex items-center text-neutral-500 dark:text-neutral-400 text-sm space-x-2 py-2">
          <MdLocationPin className="w-5 h-5 " />
          <span className="!mr-0">{address || "N/A"}</span>
        </div>
        <div className="flex items-center justify-between py-2">
          <Badge name={property_type} className="text-xs" />
          <Badge name={property_category} className="text-xs" />
        </div>
        <div className="mb-2">
          <Badge name={`${totalBedrooms || "N/A"} Beds`} className="text-xs" />
        </div>
        <div className="flex justify-between items-center">
          <span className="text-base font-semibold">
            {`${currencySymbol()} ${Number(price)?.toFixed(2) || "N/A"}`}
            {size === "default" && (
              <span className="text-sm text-neutral-500 dark:text-neutral-400 font-normal">
                /month
              </span>
            )}
          </span>
          {!!reviewStart && (
            <StartRating reviewCount={reviewCount} reviewTotal={reviewTotal} />
          )}
        </div>
        <div className="flex items-center w-full mt-4 gap-2">
          <div title="Edit Listing" className="flex-1">
            <ButtonSecondary href={`/add-listing-1/${slug}`} className="w-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </ButtonSecondary>
          </div>
          <div title="View Listing Details" className="flex-1">
            <ButtonPrimary href={`/listing-stay-detail/${slug}`} className="w-full">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </ButtonPrimary>
          </div>
          <div title={active ? "Pause Listing" : "Resume Listing"} className="flex-1">
            <ButtonPrimary
              href=""
              loading={activeLoading}
              className="w-full"
              onClick={handleToggleListingStatus}            >
              {active ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M10 4H6v16h4V4zm8 0h-4v16h4V4z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M5 3l14 9-14 9V3z"
                  />
                </svg>
              )}
            </ButtonPrimary>
          </div>
        </div>

      </div>
    );
  };
  return (
    <div
      className={`nc-StayCard group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden will-change-transform hover:shadow-xl transition-shadow ${className}`}
      data-nc-id="StayCard"
    >
      {renderSliderGallery()}
      {renderContent()}
    </div>
  );
};

export default StayCardV5;
